import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import PortalShell from "@/components/PortalShell";
import { useAuth } from "@/context/AuthContext";

function StatusBadge({ status }) {
  const map = {
    en_cours: { label: "En cours", cls: "bg-[#ffd600] text-black" },
    termine: { label: "Terminé", cls: "bg-emerald-500 text-black" },
    en_attente: { label: "En attente", cls: "bg-neutral-600 text-white" },
  };
  const v = map[status] || map.en_cours;
  return <span className={`font-display uppercase tracking-widest text-[10px] px-2 py-1 ${v.cls}`}>{v.label}</span>;
}

export function EspaceClientList() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then((r) => { setProjects(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <PortalShell role="client">
      <div data-testid="client-dashboard">
        <div className="eyebrow mb-2">/ Tableau de bord</div>
        <h1 className="font-display uppercase font-bold text-4xl md:text-5xl tracking-tight">
          Bonjour, <span className="text-[#ffd600]">{user?.name?.split(" ")[0]}</span>.
        </h1>
        <p className="text-neutral-400 mt-3 max-w-xl">
          Voici l’état d’avancement de vos chantiers DCR-BATI.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          {loading && <div className="text-neutral-500">Chargement…</div>}
          {!loading && projects.length === 0 && (
            <div className="brut-card md:col-span-2" data-testid="no-projects">
              <div className="font-display uppercase text-xl">Aucun chantier pour le moment.</div>
              <p className="text-neutral-400 mt-2">Vous n’avez pas encore de chantier suivi dans votre espace.</p>
              <Link to="/devis" className="btn-primary mt-5 inline-flex">Demander un devis <i className="fa-solid fa-arrow-right" /></Link>
            </div>
          )}
          {projects.map((p) => (
            <Link key={p.id} to={`/espace-client/chantier/${p.id}`} className="brut-card group" data-testid={`project-${p.id}`}>
              <div className="flex items-start justify-between">
                <StatusBadge status={p.status} />
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{p.location}</span>
              </div>
              <h3 className="font-display uppercase text-2xl mt-5 leading-tight">{p.name}</h3>
              <p className="text-neutral-400 text-sm mt-2 line-clamp-2">{p.description}</p>

              <div className="mt-5">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">Avancement</span>
                  <span className="font-display text-[#ffd600]">{p.progress}%</span>
                </div>
                <div className="h-2 bg-neutral-800 relative">
                  <div className="absolute top-0 left-0 h-full bg-[#ffd600]" style={{ width: `${p.progress}%` }} />
                </div>
              </div>
              <div className="mt-5 font-display uppercase text-[#ffd600] tracking-widest text-xs flex items-center gap-2 opacity-80 group-hover:opacity-100">
                Voir le suivi <i className="fa-solid fa-arrow-right" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </PortalShell>
  );
}

export function EspaceClientDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [tab, setTab] = useState("timeline");
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => {});
    api.get(`/projects/${id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
    api.get(`/projects/${id}/documents`).then((r) => setDocuments(r.data)).catch(() => {});
  }, [id]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    const { data } = await api.post(`/projects/${id}/messages`, { content: newMsg });
    setMessages((m) => [...m, data]);
    setNewMsg("");
  };

  if (!project) return <PortalShell role={user?.role}><div className="text-neutral-500">Chargement…</div></PortalShell>;

  return (
    <PortalShell role={user?.role}>
      <div data-testid="project-detail">
        <Link to={user?.role === "admin" ? "/admin/chantiers" : "/espace-client"} className="font-mono-tech text-xs uppercase tracking-widest text-neutral-500 hover:text-[#ffd600]">
          ← Retour aux chantiers
        </Link>
        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <StatusBadge status={project.status} />
            <h1 className="font-display uppercase font-bold text-3xl md:text-5xl tracking-tight mt-3">{project.name}</h1>
            <div className="font-mono-tech text-xs uppercase tracking-widest text-neutral-400 mt-2">{project.location}</div>
          </div>
          <div className="brut-card !p-5 min-w-[220px]">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">Avancement global</div>
            <div className="font-display text-5xl text-[#ffd600] mt-1">{project.progress}%</div>
            <div className="h-1.5 bg-neutral-800 mt-3 relative">
              <div className="absolute top-0 left-0 h-full bg-[#ffd600]" style={{ width: `${project.progress}%` }} />
            </div>
          </div>
        </div>

        <p className="text-neutral-300 mt-6 max-w-3xl">{project.description}</p>

        {/* Tabs */}
        <div className="mt-10 flex gap-2 border-b border-white/5">
          {[
            { id: "timeline", label: "Avancement", icon: "fa-solid fa-list-check" },
            { id: "photos", label: "Photos", icon: "fa-solid fa-images" },
            { id: "docs", label: "Documents", icon: "fa-solid fa-file-lines" },
            { id: "messages", label: "Messagerie", icon: "fa-solid fa-comments" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`font-display uppercase tracking-widest text-xs py-3 px-5 border-b-2 transition-colors ${
                tab === t.id ? "text-[#ffd600] border-[#ffd600]" : "text-neutral-400 border-transparent hover:text-white"
              }`}
            >
              <i className={`${t.icon} mr-2`} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "timeline" && (
            <div className="space-y-4" data-testid="timeline">
              {project.steps.map((s, i) => (
                <div key={s.title ? `${s.title}-${i}` : `step-${i}`} className={`brut-card !p-5 flex items-start gap-4 ${s.completed ? "border-[#ffd600]/30" : ""}`}>
                  <div className={`w-10 h-10 shrink-0 flex items-center justify-center font-display ${s.completed ? "bg-[#ffd600] text-black" : "bg-neutral-800 text-neutral-400"}`}>
                    {s.completed ? <i className="fa-solid fa-check" /> : i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-display uppercase text-lg">{s.title}</div>
                      <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{s.date}</div>
                    </div>
                    <div className="text-neutral-400 text-sm mt-1">{s.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "photos" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="photos">
              {project.photos.map((src, i) => (
                <div key={`${src}-${i}`} className="relative overflow-hidden border border-[#262626]">
                  <img src={src} alt="" className="w-full h-64 object-cover" />
                  <div className="absolute top-3 left-3 font-mono-tech text-[10px] uppercase tracking-widest bg-black/70 px-2 py-1 border border-white/10">Photo {i + 1}</div>
                </div>
              ))}
            </div>
          )}

          {tab === "docs" && (
            <div className="space-y-3" data-testid="documents">
              {documents.length === 0 && <div className="text-neutral-500">Aucun document partagé pour le moment.</div>}
              {documents.map((d) => (
                <div key={d.id} className="brut-card !p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#ffd600]/10 border border-[#ffd600]/40 flex items-center justify-center text-[#ffd600]">
                      <i className="fa-solid fa-file-lines" />
                    </div>
                    <div>
                      <div className="font-display uppercase">{d.name}</div>
                      <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{d.kind}</div>
                    </div>
                  </div>
                  <a href={d.url} className="btn-ghost !py-2 !px-3 !text-xs" target="_blank" rel="noreferrer">
                    <i className="fa-solid fa-download" /> Télécharger
                  </a>
                </div>
              ))}
            </div>
          )}

          {tab === "messages" && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-5" data-testid="messages">
              <div className="md:col-span-8 brut-card !p-0 overflow-hidden flex flex-col h-[520px]">
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {messages.length === 0 && <div className="text-neutral-500">Aucun message.</div>}
                  {messages.map((m) => {
                    const mine = m.author_id === user?.id;
                    return (
                      <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                          {m.author_name} · {m.author_role === "admin" ? "Équipe DCR" : "Client"}
                        </div>
                        <div className={`px-4 py-3 max-w-[80%] ${mine ? "bg-[#ffd600] text-black" : "bg-[#1a1a1a] text-white border border-[#262626]"}`}>
                          {m.content}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <form onSubmit={sendMessage} className="border-t border-white/5 p-3 flex gap-2">
                  <input value={newMsg} onChange={(e) => setNewMsg(e.target.value)} placeholder="Écrire un message…" className="input-field flex-1" data-testid="message-input" />
                  <button type="submit" className="btn-primary !py-3 !px-4" data-testid="message-send">
                    <i className="fa-solid fa-paper-plane" />
                  </button>
                </form>
              </div>
              <div className="md:col-span-4 brut-card">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#ffd600]">/ Contact chantier</div>
                <div className="font-display uppercase mt-3 text-lg">Conducteur de travaux</div>
                <div className="text-neutral-400 text-sm">Disponible 7h30 – 18h00</div>
                <div className="text-neutral-300 text-sm mt-4">06 51 02 60 78<br />contact@dcrbati.fr</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PortalShell>
  );
}
